// Video docs → shared NameScript convergence (§10.2). Converts per-episode video
// docs into the SHARED NameScript so the common tail (assignProvisionalIds →
// govern → renumber → validate) produces the exact same v3 as text ingest.
// applyRename folds the serial pipeline's provisional→real alias map into the
// structured name fields; the deterministic finalize sweep owns that map.

import type { NameScene, NameScript } from "./assembler.js";
import type { NameStateRef, SpeakerRef } from "./records.js";
import { isCrowdName } from "./serial-context.js";
import type { VideoEpisodeDoc } from "./video-records.js";
import { normalizeSceneTime } from "./environment.js";
import { normalizeVideoTimestamp } from "./video-timestamp.js";

// Rename a name, preserving a typed speaker prefix (broadcast:xxx → only xxx).
export function applyRename(name: string | undefined, map: Map<string, string>): string | undefined {
  if (!name) return name;
  const m = name.match(/^(\w+):(.+)$/);
  if (m) return `${m[1]}:${map.get(m[2]!) ?? m[2]}`;
  return map.get(name) ?? name;
}

const pad = (n: number): string => String(n).padStart(3, "0");
const SPACE_ZH_TO_EN: Record<string, string> = { "内": "interior", "外": "exterior" };
const KIND_TO_TYPE: Record<string, string> = { act: "action", dlg: "dialogue", think: "inner_thought" };
// scaffold typed-speaker prefixes → v3 non-actor speaker kinds.
const KIND_MAP: Record<string, "system" | "broadcast" | "offscreen" | "group"> = {
  broadcast: "broadcast", system: "system", group: "group", offscreen: "offscreen", prop: "offscreen", location: "offscreen",
};

function toSpeaker(raw: string): SpeakerRef {
  const m = raw.match(/^(\w+):(.+)$/);
  if (m && KIND_MAP[m[1]!]) { const label = m[2]!.trim(); return label ? { kind: KIND_MAP[m[1]!]!, label } : { kind: KIND_MAP[m[1]!]! }; }
  if (isCrowdName(raw)) return { kind: "group", label: raw.trim() };
  return { name: raw };
}

// Convert video docs → the shared NameScript (name-based, plain-text content,
// scene-level cast). Timestamps ride action.extend.timestamp through to the final
// v3. Actor speakers are added to the scene cast (mirrors assemble()).
export function docsToNameScript(docs: VideoEpisodeDoc[], title: string): NameScript {
  let scn = 0;
  const episodes = docs.map((d, ei) => {
    const episodeIdentityKey = `video:episode:${d.episode}`;
    const sceneIdentityOccurrences = new Map<string, number>();
    return {
      identity_key: episodeIdentityKey,
      episode_id: `ep_${pad(ei + 1)}`,
      scenes: d.scenes.map((s): NameScene => {
        const sceneAnchor = s.timeRange?.trim()
          ? `time:${s.timeRange.trim()}`
          : `index:${s.index}`;
        const sceneOccurrence = (sceneIdentityOccurrences.get(sceneAnchor) ?? 0) + 1;
        sceneIdentityOccurrences.set(sceneAnchor, sceneOccurrence);
        const sceneIdentityKey = `${episodeIdentityKey}:scene:${sceneAnchor}:${sceneOccurrence}`;
        const env: { space?: string; time?: string } = {};
        const sp = SPACE_ZH_TO_EN[(s.environment.space ?? "").trim()];
        const tm = normalizeSceneTime(s.environment.time);
        if (sp) env.space = sp;
        if (tm) env.time = tm;

        const actors: NameStateRef[] = [];
        const ensureSpeakerActor = (name: string): void => {
          if (!actors.some((actor) => actor.name === name)) actors.push({ name });
        };
        for (const ch of s.characters) {
          if (ch.name && !isCrowdName(ch.name)) {
            if (ch.states.length === 0) actors.push({ name: ch.name });
            else actors.push(...ch.states.map((state) => ({ name: ch.name, state })));
          }
        }

        const actionIdentityOccurrences = new Map<string, number>();
        const actions = s.anchors.map((a) => {
          const timestamp = normalizeVideoTimestamp(a.ts);
          const actionAnchor = timestamp
            ? `time:${timestamp}`
            : `content:${JSON.stringify([a.kind, a.speaker ?? "", a.content])}`;
          const actionOccurrence = (actionIdentityOccurrences.get(actionAnchor) ?? 0) + 1;
          actionIdentityOccurrences.set(actionAnchor, actionOccurrence);
          const out: NameScene["actions"][number] = {
            identity_key: `${sceneIdentityKey}:action:${actionAnchor}:${actionOccurrence}`,
            type: KIND_TO_TYPE[a.kind] ?? "action",
            content: a.content,
          };
          if ((a.kind === "dlg" || a.kind === "think") && a.speaker) {
            const spk = toSpeaker(a.speaker);
            out.speaker = spk;
            if ("name" in spk) ensureSpeakerActor(spk.name); // actor speaker is present
          }
          if (a.parenthetical) out.parenthetical = a.parenthetical;
          const ts = normalizeVideoTimestamp(a.ts);
          if (ts) out.extend = { timestamp: ts };
          return out;
        });

        const loc = (s.location ?? "").trim();
        // Scene props must be unique by normalized name: Script v3 forbids
        // duplicate prop_id on one scene, and the video model occasionally
        // lists the same prop twice (or alias-collapsed names become one).
        const props: NameStateRef[] = [];
        const seenProps = new Set<string>();
        for (const p of s.props ?? []) {
          if (!p) continue;
          const key = p.normalize("NFC").replace(/\s+/g, " ").trim();
          if (!key || seenProps.has(key)) continue;
          seenProps.add(key);
          props.push({ name: p });
        }
        return {
          identity_key: sceneIdentityKey,
          scene_id: `scn_${pad(++scn)}`,
          ...(env.space || env.time ? { environment: env } : {}),
          location: loc ? { name: loc } : null,
          actors,
          props,
          actions,
        };
      }),
    };
  });
  return { title, episodes };
}
